import { UIComponent, OSSchema, ScreenTemplate, ScreenField, resolveScreenNoun, titleCaseWords } from "@sloe/schema";
import { generateSeedData } from "../../lib/seed-data-generator";
import { DEFAULT_BRAND_COLOR } from "../../lib/brand";
import { EmptyState } from "./EmptyState";
import { Minus, ArrowUpRight, GripVertical, Search, Plus, ArrowRight, Plug, Briefcase, Image as ImageIcon, CalendarDays, Package, Bell, Flame, ThermometerSun, Snowflake, CircleSlash, Circle, ScanSearch, History, CircleDot, BadgeCheck, Network, Upload } from "lucide-react";
import { useState, useMemo, useEffect, type FormEvent, type ReactNode } from "react";
import { useOsStore } from "../../lib/os-store";
import { renderField, inferFields } from "./field-renderers";
import { CreateRecordModal } from "./CreateRecordModal";
import { BulkImportModal } from "./BulkImportModal";
import { useReloadGuard } from "../../lib/update-reload";

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT RENDERER
// ═══════════════════════════════════════════════════════════════════════
// Routes each UIComponent type to a real, beautiful, populated renderer.
// No more "[kpi_card] Bound to: {}". Every component is alive.
// ═══════════════════════════════════════════════════════════════════════

interface ComponentRendererProps {
  component: UIComponent;
  schema: OSSchema;
  screen: ScreenTemplate;
  /**
   * The Agentic Operating System UUID (operating_systems.id). Required for any API call that
   * uses os_id as a foreign key (metrics execute, drawer telemetry).
   * Do NOT substitute schema.id here — that's the Architect's schema
   * version identifier (e.g. "schema-kingkay-test-v1"), not a uuid.
   */
  workspaceId: string;
  onItemClick?: (item: any) => void;
  /**
   * Real records from /api/screen-data, flattened to {id, ...data}. When
   * provided, table + kanban use these instead of seed records. KPI cards
   * stay seed-driven for now (not in screen_data yet).
   */
  records?: any[];
  /**
   * Label of the currently-selected KPI on this screen (when one has been
   * clicked). Drives the "context anchored" highlight on KPI cards so the
   * user can see which KPI the Operator is grounded on.
   */
  selectedKpiLabel?: string | null;
  /**
   * Position-indexed seed kpi (label/value/trend/change/color). Supplies
   * visual chrome for live KPI cards (sparkline tint, trend icon) until
   * real trend data lands. Passed from ScreenContent based on the kpi_card
   * component's index among its siblings.
   */
  kpiSeedFallback?: { label: string; color: string };
  filterState?: { field: string; value: string | null } | null;
  onFilterChange?: (field: string, value: string | null) => void;
  searchState?: { field: string; query: string } | null;
  onSearchChange?: (field: string, query: string) => void;
  onNavigateScreen?: (screenId: string) => void;
}

function screenCopyNoun(screen: ScreenTemplate | undefined, fallback = "record") {
  return resolveScreenNoun(screen, fallback);
}

function previewFields(fields: ScreenField[], max = 5): ScreenField[] {
  return fields.filter((field) => field.key !== "id").slice(0, max);
}

function SkeletonBar({
  className = "",
}: {
  className?: string;
}) {
  return <div className={`h-2.5 rounded-full bg-slate-200/80 ${className}`} />;
}

function StructuredRecordsEmptyState({
  title,
  message,
  preview,
  children,
}: {
  title: string;
  message: string;
  preview: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="col-span-full rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-5">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-xs text-slate-500">{message}</p>
      </div>
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-3">
        {preview}
      </div>
      {children && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {children}
        </div>
      )}
    </div>
  );
}

function GhostTablePreview({ fields }: { fields: ScreenField[] }) {
  const cols = previewFields(fields);
  return (
    <div className="overflow-hidden rounded-lg border border-white/80 bg-white/80">
      <table className="w-full table-fixed">
        <thead>
          <tr className="border-b border-slate-100 bg-white/70">
            {cols.map((field) => (
              <th
                key={field.key}
                className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 ${
                  field.type === "currency" ? "text-right" : "text-left"
                }`}
              >
                {field.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100/80">
          {Array.from({ length: 3 }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {cols.map((field, colIndex) => (
                <td
                  key={field.key}
                  className={`px-4 py-4 ${field.type === "currency" ? "text-right" : ""}`}
                >
                  {colIndex === 0 ? (
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg border border-slate-200 bg-slate-100/90" />
                      <SkeletonBar className="h-3 w-24 sm:w-32" />
                    </div>
                  ) : field.type === "select" ? (
                    <div className="flex justify-start">
                      <SkeletonBar className="h-6 w-16 rounded-full" />
                    </div>
                  ) : field.type === "date" ? (
                    <div className="flex justify-start">
                      <SkeletonBar className="w-20" />
                    </div>
                  ) : field.type === "currency" ? (
                    <div className="flex justify-end">
                      <SkeletonBar className="w-16" />
                    </div>
                  ) : (
                    <SkeletonBar className="w-24" />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GhostListPreview({ fields }: { fields: ScreenField[] }) {
  const cols = previewFields(fields, 3);
  const primary = cols[0];
  const secondary = cols.slice(1);
  return (
    <div className="overflow-hidden rounded-lg border border-white/80 bg-white/80">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        {primary && (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            {primary.label}
          </span>
        )}
        {secondary.map((field) => (
          <span
            key={field.key}
            className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400"
          >
            {field.label}
          </span>
        ))}
      </div>
      <ul className="divide-y divide-slate-100/80">
        {Array.from({ length: 3 }).map((_, rowIndex) => (
          <li key={rowIndex} className="flex items-center gap-4 px-4 py-4">
            <div className="h-9 w-9 rounded-lg border border-slate-200 bg-slate-100/90 shrink-0" />
            <div className="min-w-0 flex-1">
              <SkeletonBar className="h-3 w-28 sm:w-36" />
              <div className="mt-2 flex flex-wrap gap-2">
                {secondary.length > 0 ? (
                  secondary.map((field) => (
                    <SkeletonBar
                      key={field.key}
                      className={field.type === "date" ? "w-16" : "w-20"}
                    />
                  ))
                ) : (
                  <SkeletonBar className="w-24" />
                )}
              </div>
            </div>
            <div className="h-4 w-4 rounded-full border border-slate-200 bg-slate-100/90 shrink-0" />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ComponentRenderer({
  component,
  schema,
  screen,
  workspaceId,
  onItemClick,
  records,
  selectedKpiLabel,
  kpiSeedFallback,
  filterState,
  onFilterChange,
  searchState,
  onSearchChange,
  onNavigateScreen,
}: ComponentRendererProps) {
  const seedData = useMemo(
    () => generateSeedData(schema.metadata.industry_shape, screen.label),
    [schema.metadata.industry_shape, screen.label]
  );

  switch (component.type) {
    case "kpi_card":
      return <KPICardRenderer component={component} workspaceId={workspaceId} seedFallback={kpiSeedFallback} onItemClick={onItemClick} selectedKpiLabel={selectedKpiLabel} />;
    case "table":
      return <DataTableRenderer seedData={seedData} records={records} workspaceId={workspaceId} screen={screen} schema={schema} />;
    case "chart":
      return <ChartRenderer seedData={seedData} schema={schema} config={component.config} />;
    case "image_gallery":
      return <ImageGalleryRenderer schema={schema} />;
    case "text_block":
      return <TextBlockRenderer component={component} />;
    case "form":
      return <FormRenderer schema={schema} screen={screen} workspaceId={workspaceId} />;
    // Phase 5-A — types emitted by buildShapeSkeleton (claim-bundle.ts).
    case "list":
      return <ListRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} />;
    case "filter":
      return <FilterRenderer component={component} records={records} filterState={filterState} onFilterChange={onFilterChange} />;
    case "search":
      return <SearchRenderer component={component} searchState={searchState} onSearchChange={onSearchChange} />;
    case "kanban":
      return <KanbanRenderer seedData={seedData} records={records} schema={schema} workspaceId={workspaceId} screen={screen} onItemClick={onItemClick} />;
    case "stat_row":
      return <StatRowRenderer component={component} records={records} screen={screen} />;
    case "brand_panel":
      return <BrandPanelRenderer schema={schema} onManage={onNavigateScreen ? () => onNavigateScreen("settings") : undefined} />;
    case "integration_list":
      return <IntegrationListRenderer workspaceId={workspaceId} onManage={onNavigateScreen ? () => onNavigateScreen("integrations") : undefined} />;
    // Phase 5-D — industry-shape-aware grids.
    case "portfolio_card_grid":
      return <PortfolioCardRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} onNavigateScreen={onNavigateScreen} />;
    case "gallery_card_grid":
      return <GalleryCardRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} onNavigateScreen={onNavigateScreen} />;
    case "schedule_block_grid":
      return <ScheduleBlockRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} onNavigateScreen={onNavigateScreen} />;
    case "production_run_grid":
      return <ProductionRunRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} onNavigateScreen={onNavigateScreen} />;
    // R3 — ranked alerts + drafted next action.
    case "urgent_actions":
      return <UrgentActionsRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} />;
    // R3 #2 — Qualifier verdict: named buckets + fit/intent.
    case "scored_buckets":
      return <ScoredBucketsRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} />;
    // R3 #3 — Domain Tracker: per-entity development timeline.
    case "development_timeline":
      return <DevelopmentTimelineRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} />;
    // R3 #4 — Router: roster of specialist cards.
    case "specialist_status":
      return <SpecialistStatusRenderer component={component} records={records} screen={screen} workspaceId={workspaceId} schema={schema} />;
    default:
      return <KPICardRenderer component={component} workspaceId={workspaceId} seedFallback={kpiSeedFallback} onItemClick={onItemClick} selectedKpiLabel={selectedKpiLabel} />;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// KPI CARD RENDERER
// ═══════════════════════════════════════════════════════════════════════

/**
 * Renders ONE kpi card. The screen-level grid moved to ScreenContent —
 * each kpi_card component is its own tile in that grid.
 *
 * Two modes, picked from the component's shape:
 *   - metric_key on the component (top-level) → <LiveKPICard /> fetches
 *     via /api/metrics/execute. Display label comes from data_mapping.label
 *     (falling back to the metric's own label after fetch). Visual chrome
 *     (trend / change / color / sparkline) borrows from seedFallback until
 *     real trend data lands.
 *   - no metric_key → a single seed-styled card. Lets unmigrated kpi_card
 *     placeholders still render something instead of a blank tile.
 */
function KPICardRenderer({ component, workspaceId, seedFallback, onItemClick, selectedKpiLabel }: { component: UIComponent; workspaceId: string; seedFallback?: { label: string; color: string }; onItemClick?: (item: any) => void; selectedKpiLabel?: string | null }) {
  const metricKey =
    ((component as any).metric_key as string | undefined) ||
    ((component.data_mapping as any)?.metric_key as string | undefined);
  const labelOverride =
    ((component.data_mapping as any)?.label as string | undefined) || undefined;

  if (metricKey) {
    return (
      <LiveKPICard
        workspaceId={workspaceId}
        metricKey={metricKey}
        labelOverride={labelOverride}
        seedFallback={seedFallback}
        onItemClick={onItemClick}
        selectedKpiLabel={selectedKpiLabel}
      />
    );
  }

  // No metric_key → seed-style placeholder card. The position-indexed seed
  // entry (passed in by ScreenContent) supplies the chrome; data_mapping
  // .label still wins for the heading so a placeholder can carry intent.
  const kpi = seedFallback || { label: labelOverride || "KPI", color: "#6366F1" };
  const label = labelOverride || kpi.label;
  const isSelected = !!selectedKpiLabel && label === selectedKpiLabel;

  // R1 — this card has NO real metric source; it only ever had the seed
  // fallback. Never render fabricated value/change/trend/sparkline. Show an
  // honest placeholder. Label (intent) and color (cosmetic) are preserved.
  return (
    <div
      onClick={() => onItemClick?.({ kind: "kpi", label, value: "—", trend: "flat", change: "" })}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected || undefined}
      aria-label={isSelected ? `${label} — selected as Operator context` : `${label}, click to set as Operator context`}
      className={`rounded-2xl border p-6 transition-all duration-300 group cursor-pointer ${
        isSelected
          ? "border-indigo-300 ring-2 ring-indigo-200 bg-indigo-50/40 shadow-md"
          : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 hover:shadow-lg"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-50 group-hover:bg-slate-100 transition-colors">
          <Minus className="w-4 h-4 text-slate-400" />
        </div>
      </div>
      <div className="text-3xl font-bold text-slate-900 mb-1" style={{ color: kpi.color }}>
        —
      </div>
      <div className="text-xs font-medium text-slate-400">
        Connect data to populate
      </div>
      {/* R1 — flat neutral baseline; no random sparkline (no data to chart yet) */}
      <div className="mt-4 flex items-end h-8">
        <div className="w-full h-px bg-slate-200" />
      </div>
    </div>
  );
}

// ─── Live KPI card (fetches its own metric on mount) ─────────────────────

interface MetricResponse {
  value: number | null;
  label: string;
  unit?: string | null;
}

type LiveKpiState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: MetricResponse };

function formatMetricValue(value: number | null, unit?: string | null): string {
  if (value == null) return "—";
  switch (unit) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value);
    case "percent":
      return `${value}%`;
    case "count":
    case "number":
    default:
      return Number(value).toLocaleString();
  }
}

function LiveKPICard({
  workspaceId,
  metricKey,
  labelOverride,
  seedFallback,
  onItemClick,
  selectedKpiLabel,
}: {
  workspaceId: string;
  metricKey: string;
  labelOverride?: string;
  seedFallback?: { label: string; color: string };
  onItemClick?: (item: any) => void;
  selectedKpiLabel?: string | null;
}) {
  const [state, setState] = useState<LiveKpiState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/metrics/execute", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ workspace_id: workspaceId, metric_key: metricKey }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data?.ok) {
          setState({ status: "error", message: data?.error || `HTTP ${res.status}` });
          return;
        }
        setState({ status: "ok", data: { value: data.value, label: data.label, unit: data.unit } });
      } catch (err: any) {
        if (cancelled) return;
        setState({ status: "error", message: err?.message || "Network error" });
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, metricKey]);

  // R1 — keep the REAL fetched value, but never fabricate trend/change from
  // the seed fallback. No real trend history exists in v1, so trend is flat
  // (neutral Minus, no fake arrow) and change is blank. When metric_executions
  // history lands, real trend/change can replace these. color is cosmetic-only
  // (not data), so the seed color is retained purely for styling.
  const label =
    labelOverride ||
    (state.status === "ok" ? state.data.label : seedFallback?.label || metricKey);
  const trend = "flat" as const;
  const change = "";
  const color = seedFallback?.color || "#6366F1";

  const isSelected = !!selectedKpiLabel && label === selectedKpiLabel;

  const valueDisplay =
    state.status === "ok"
      ? formatMetricValue(state.data.value, state.data.unit)
      : state.status === "error"
        ? "—"
        : null; // loading

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected || undefined}
      aria-label={isSelected ? `${label} — selected as Operator context` : `${label}, click to set as Operator context`}
      title={state.status === "error" ? `Couldn't load: ${state.message}` : undefined}
      onClick={() => onItemClick?.({ kind: "kpi", label, value: valueDisplay ?? "—", trend, change })}
      className={`rounded-2xl border p-6 transition-all duration-300 group cursor-pointer ${
        isSelected
          ? "border-indigo-300 ring-2 ring-indigo-200 bg-indigo-50/40 shadow-md"
          : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300 hover:shadow-lg"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        {/* R1 — neutral marker only; no real trend direction exists in v1 */}
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-50 group-hover:bg-slate-100 transition-colors">
          <Minus className="w-4 h-4 text-slate-400" />
        </div>
      </div>
      <div className="text-3xl font-bold text-slate-900 mb-1 min-h-[2.25rem]" style={{ color }}>
        {valueDisplay === null
          ? <span className="inline-block h-9 w-20 rounded bg-slate-100 animate-pulse align-middle" aria-label="Loading" />
          : valueDisplay}
      </div>
      <div className="text-xs font-medium text-slate-500">
        {change || " "}
      </div>
      {/* R1 — flat neutral baseline; no random sparkline until real trend history exists */}
      <div className="mt-4 flex items-end h-8">
        <div className="w-full h-px bg-slate-200" />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DATA TABLE RENDERER
// ═══════════════════════════════════════════════════════════════════════

function DataTableRenderer({ seedData, records, workspaceId, screen, schema }: { seedData: ReturnType<typeof generateSeedData>; records?: any[]; workspaceId: string; screen: ScreenTemplate; schema?: OSSchema }) {
  const [searchQuery, setSearchQuery] = useState("");
  // CRUD-2 — Add button opens the same modal CRUD-1 wired into ListRenderer.
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const noun = screenCopyNoun(screen, "record");
  const singular = noun.singular;
  const singularLabel = titleCaseWords(singular);
  // DESIGN-1 — brand color for the Add button + modal Create button.
  const brandColor = schema?.metadata?.brand_color;

  // B1-FIX — never fall back to fabricated seed data. A fresh screen
  // shows an empty state below. seedData stays in the props signature
  // for legacy callers but is no longer read here.
  const source = records ?? [];

  // Schema-driven columns. If screen.fields[] is missing, infer columns
  // from the first row so unmigrated screens still render usefully.
  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0)
      ? screen.fields
      : (source.length > 0 ? inferFields(source[0]) : []);

  // Search across every field's string-cast value — robust to any schema.
  const filteredRecords = source.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return fields.some(f => String(r[f.key] ?? "").toLowerCase().includes(q));
  });

  return (
    <>
      {source.length === 0 ? (
        fields.length > 0 ? (
          <StructuredRecordsEmptyState
            title={`Your ${singular} list is ready`}
            message={`Add your first ${singular} and it shows here.`}
            preview={<GhostTablePreview fields={fields} />}
          >
            <button
              type="button"
              onClick={() => setBulkImportOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Upload className="w-4 h-4" /> Bring in existing {noun.plural}
            </button>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              style={brandColor ? { backgroundColor: brandColor } : undefined}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium ${
                brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
              }`}
            >
              <Plus className="w-4 h-4" /> Add {singular}
            </button>
          </StructuredRecordsEmptyState>
        ) : (
          <EmptyState
            title={`Your ${singular} list is ready`}
            message={`Add your first ${singular} and it shows here.`}
            noun={singular}
          >
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setBulkImportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Upload className="w-4 h-4" /> Bring in existing {noun.plural}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                style={brandColor ? { backgroundColor: brandColor } : undefined}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium ${
                  brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
                }`}
              >
                <Plus className="w-4 h-4" /> Add {singular}
              </button>
            </div>
          </EmptyState>
        )
      ) : (
        <div className="col-span-full bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">{screen.label} Records</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBulkImportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Upload className="w-3.5 h-3.5" /> Import
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                style={brandColor ? { backgroundColor: brandColor } : undefined}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold ${
                  brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
                }`}
              >
                <Plus className="w-3.5 h-3.5" /> Add {singular}
              </button>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80">
                  {fields.map((f) => (
                    <th
                      key={f.key}
                      // EVOLUTION-3e — Spotlight anchor target. List + kanban
                      // layouts don't have per-field uniform anchors today;
                      // Spotlight silently dismisses on those screens.
                      data-field-key={f.key}
                      className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${f.type === "currency" ? "text-right" : "text-left"}`}
                    >
                      {f.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRecords.map(record => (
                  <tr
                    key={record.id}
                    onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
                    className="hover:bg-slate-50/50 transition-colors cursor-pointer group"
                  >
                    {fields.map((f, idx) => (
                      <td
                        key={f.key}
                        className={`px-6 py-4 ${f.type === "currency" ? "text-right" : ""}`}
                      >
                        {idx === 0 ? (
                          // Primary cell: avatar-square + emphasized name.
                          // Kept as decoration carried over from the previous
                          // hardcoded table; works for any first-field value.
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                              {String(record[f.key] ?? "?").charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                              {String(record[f.key] ?? "—")}
                            </span>
                          </div>
                        ) : (
                          renderField(f, record)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <CreateRecordModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => useOsStore.getState().bumpScreenRefresh()}
        workspaceId={workspaceId}
        screenId={screen.screen_id}
        fields={fields}
        title={`New ${singularLabel}`}
        existingRecords={source}
        brandColor={brandColor}
      />
      <BulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImported={() => useOsStore.getState().bumpScreenRefresh()}
        workspaceId={workspaceId}
        screenId={screen.screen_id}
        screenLabel={screen.label}
        noun={noun}
        fields={fields}
        brandColor={brandColor}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CHART RENDERER (Bar chart via CSS)
// ═══════════════════════════════════════════════════════════════════════

function ChartRenderer({ seedData, schema, config }: { seedData: ReturnType<typeof generateSeedData>; schema: OSSchema; config?: any }) {
  // R1 — ChartRenderer has no real data series in v1. It previously filled the
  // chart with randomized bar heights — fabricated data shown as a real
  // performance trend. Until a real series is wired, render an honest empty
  // state instead of inventing numbers. (seedData/schema/config retained in the
  // signature for the future real-data path.)
  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-800 mb-6">Performance Overview</h3>
      <div className="h-48 flex flex-col items-center justify-center text-center">
        <div className="w-full h-px bg-slate-200 mb-4" />
        <p className="text-sm font-medium text-slate-500">No data yet</p>
        <p className="text-xs text-slate-400 mt-1">Connect data to populate</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// KANBAN BOARD RENDERER
// ═══════════════════════════════════════════════════════════════════════

export function KanbanRenderer({ seedData, records, schema, workspaceId, screen, onItemClick }: { seedData: ReturnType<typeof generateSeedData>; records?: any[]; schema: OSSchema; workspaceId: string; screen?: ScreenTemplate; onItemClick?: (item: any) => void }) {
  // B1-FIX — never fall back to fabricated seed data. Empty state
  // renders below. seedData stays in props for legacy callers.
  const sourceRecords = records ?? [];

  // CRUD-2 — Add button + modal, only wired when `screen` is present
  // (it's optional in the signature; legacy callers without it skip both).
  // B1-FIX — hoisted above the empty-state early return so both branches
  // can reach setModalOpen / brandColor / singular without recomputing.
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const screenNoun = screenCopyNoun(screen, "record");
  const singular = screenNoun.singular;
  const singularLabel = titleCaseWords(singular);
  // DESIGN-1 — brand color sourced from schema (always present here).
  const brandColor = schema?.metadata?.brand_color;

  // Card layout slots driven by field type affinity. If the screen has no
  // explicit fields, infer from the first row. Each slot prefers a typed
  // match; falls back to positional fields so screens without the "right"
  // types still produce a reasonable card.
  const fields: ScreenField[] =
    (screen?.fields && screen.fields.length > 0)
      ? screen.fields
      : (sourceRecords.length > 0 ? inferFields(sourceRecords[0]) : []);
  const configuredStages =
    fields.find((f) => f.key === "stage" || /stage|phase|status/i.test(f.label))?.options || [];
  const recordStages = sourceRecords.map((r) => r.stage).filter(Boolean).map(String);
  const stages = Array.from(new Set([...configuredStages, ...recordStages]));

  // B1-FIX — empty state. Mirrors DataTableRenderer + ListRenderer pattern.
  if (sourceRecords.length === 0) {
    const label = screen?.label || "items";
    return (
      <>
        <div className="col-span-full rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
              <p className="text-xs text-slate-500 mt-1">
                {stages.length > 0
                  ? `Your ${singular} board is ready. Add your first ${singular} and it'll move through these stages.`
                  : `Your ${singular} board is ready. Add your first ${singular} and it shows here.`}
              </p>
            </div>
            {screen && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setBulkImportOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="w-3.5 h-3.5" /> Bring in existing
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  style={brandColor ? { backgroundColor: brandColor } : undefined}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold ${
                    brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
                  }`}
                >
                  <Plus className="w-3.5 h-3.5" /> Add {singular}
                </button>
              </div>
            )}
          </div>
          {stages.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-2">
              {stages.map((stage) => (
                <div key={stage} className="min-w-[240px] rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-xs font-semibold text-slate-700 truncate">{stage}</h4>
                    <span className="text-[11px] font-bold text-slate-400 bg-white px-2 py-0.5 rounded-full">0</span>
                  </div>
                  <div className="mt-5 rounded-lg border border-dashed border-slate-200 bg-white/70 py-8 text-center text-xs text-slate-400">
                    {`When a ${singular} reaches ${stage}, it shows here`}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title={`Your ${singular} board is ready`}
              message={`Add your first ${singular} and it shows here.`}
              noun={singular}
            />
          )}
        </div>
        {screen && (
          <CreateRecordModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onCreated={() => useOsStore.getState().bumpScreenRefresh()}
            workspaceId={workspaceId}
            screenId={screen.screen_id}
            fields={fields}
            title={`New ${singularLabel}`}
            existingRecords={sourceRecords}
            brandColor={brandColor}
          />
        )}
        {screen && (
          <BulkImportModal
            open={bulkImportOpen}
            onClose={() => setBulkImportOpen(false)}
            onImported={() => useOsStore.getState().bumpScreenRefresh()}
            workspaceId={workspaceId}
            screenId={screen.screen_id}
            screenLabel={screen.label}
            noun={screenNoun}
            fields={fields}
            brandColor={brandColor}
          />
        )}
      </>
    );
  }

  const primaryField = fields[0];
  // Stage drives the column — never put it in the card body.
  const cardFields = fields.slice(1).filter(f => f.key !== "stage");
  const lhsField =
    cardFields.find(f => f.type === "user") ||
    cardFields[0];
  const rhsField =
    cardFields.find(f => f.type === "currency") ||
    cardFields.find(f => f.key !== lhsField?.key);
  const dateField =
    cardFields.find(f => f.type === "date" && f.key !== lhsField?.key && f.key !== rhsField?.key);
  const statusField =
    cardFields.find(f =>
      f.type === "select" &&
      f.key !== lhsField?.key &&
      f.key !== rhsField?.key &&
      f.key !== dateField?.key,
    );

  return (
    <>
    <div className="flex flex-col gap-3 h-full">
      {screen && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">{screen.label}</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkImportOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              style={brandColor ? { backgroundColor: brandColor } : undefined}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold ${
                brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
              }`}
            >
              <Plus className="w-3.5 h-3.5" /> Add {singular}
            </button>
          </div>
        </div>
      )}
    <div className="flex gap-4 overflow-x-auto pb-4 flex-1">
      {stages.map((stage) => {
        const stageRecords = sourceRecords.filter(r => r.stage === stage);
        return (
          <div key={stage} className="min-w-[280px] flex-shrink-0 bg-slate-50 rounded-2xl p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700">{stage}</h3>
              <span className="text-xs font-bold text-slate-400 bg-white px-2 py-0.5 rounded-full">{stageRecords.length}</span>
            </div>
            <div className="flex flex-col gap-3 flex-1">
              {stageRecords.map(record => (
                <div
                  key={record.id}
                  onClick={() => screen && useOsStore.getState().openDrawer(record, screen, workspaceId)}
                  className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all cursor-pointer group"
                >
                  {/* Title row */}
                  {primaryField && (
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {String(record[primaryField.key] ?? "—")}
                      </span>
                      <GripVertical className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </div>
                  )}
                  {/* Middle row: user (left) + currency (right) when present */}
                  {(lhsField || rhsField) && (
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <div className="min-w-0 flex-1">
                        {lhsField && renderField(lhsField, record)}
                      </div>
                      <div className="shrink-0">
                        {rhsField && rhsField.key !== lhsField?.key && renderField(rhsField, record)}
                      </div>
                    </div>
                  )}
                  {/* Bottom row: status pill (left) + date (right) when present */}
                  {(statusField || dateField) && (
                    <div className="flex items-center justify-between gap-2 mt-3">
                      <div>
                        {statusField && renderField(statusField, record)}
                      </div>
                      <div>
                        {dateField && renderField(dateField, record)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {stageRecords.length === 0 && (
                <div className="flex-1 flex items-center justify-center text-xs text-slate-400 italic">
                  No items
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
    </div>
    {screen && (
      <CreateRecordModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => useOsStore.getState().bumpScreenRefresh()}
        workspaceId={workspaceId}
        screenId={screen.screen_id}
        fields={fields}
        title={`New ${singularLabel}`}
        existingRecords={sourceRecords}
        brandColor={brandColor}
      />
    )}
    {screen && (
      <BulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImported={() => useOsStore.getState().bumpScreenRefresh()}
        workspaceId={workspaceId}
        screenId={screen.screen_id}
        screenLabel={screen.label}
        noun={screenNoun}
        fields={fields}
        brandColor={brandColor}
      />
    )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MINOR RENDERERS
// ═══════════════════════════════════════════════════════════════════════

function ImageGalleryRenderer({ schema }: { schema: OSSchema }) {
  const colors = ["#6366F1", "#EC4899", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444"];
  return (
    <div className="col-span-full grid grid-cols-2 md:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="aspect-square rounded-2xl overflow-hidden relative group cursor-pointer">
          <div 
            className="w-full h-full transition-transform duration-500 group-hover:scale-110"
            style={{ background: `linear-gradient(135deg, ${colors[i]}30, ${colors[i]}10)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
            <span className="text-white text-sm font-medium">Asset {i + 1}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TextBlockRenderer({ component }: { component: UIComponent }) {
  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-6">
      <p className="text-slate-700 leading-relaxed text-sm">
        {component.data_mapping?.content || "This section contains operational notes and documentation for your team. Click to edit."}
      </p>
    </div>
  );
}

export function formFieldsForScreen(screen: ScreenTemplate): ScreenField[] {
  if (screen.fields && screen.fields.length > 0) return screen.fields.filter((field) => !["id", "created_at", "updated_at"].includes(field.key));
  return [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "type", label: "Type", type: "text" },
    { key: "value", label: "Value", type: "text" },
    { key: "notes", label: "Notes", type: "text" },
  ] as ScreenField[];
}

export function normalizeFormRecord(fields: ScreenField[], values: Record<string, any>): Record<string, any> {
  const data: Record<string, any> = {};
  for (const field of fields) {
    const raw = values[field.key];
    if ((field.type as any) === "number" || field.type === "currency") {
      if (raw === "" || raw === undefined || raw === null) {
        data[field.key] = null;
      } else {
        const n = Number(raw);
        data[field.key] = Number.isFinite(n) ? n : null;
      }
    } else {
      data[field.key] = raw === undefined || raw === "" ? null : raw;
    }
  }
  return data;
}

function FormRenderer({ schema, screen, workspaceId }: { schema: OSSchema; screen: ScreenTemplate; workspaceId: string }) {
  const fields = useMemo(() => formFieldsForScreen(screen), [screen]);
  const [values, setValues] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState(false);
  const brandColor = schema.metadata.brand_color || DEFAULT_BRAND_COLOR;
  const singular = titleCaseWords(screenCopyNoun(screen, "record").singular);
  const isDirty = fields.some((field) => {
    const value = values[field.key];
    return value !== undefined && value !== null && value !== "";
  });

  useReloadGuard({
    id: `inline-form:${workspaceId}:${screen.screen_id}`,
    active: true,
    busy: submitting,
    dirty: isDirty,
    label: `Add ${singular}`,
  });

  useEffect(() => {
    const initial: Record<string, any> = {};
    for (const field of fields) initial[field.key] = "";
    setValues(initial);
    setError(null);
    setCreated(false);
  }, [fields]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    setCreated(false);

    try {
      const res = await fetch("/api/screen-data", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_id: workspaceId,
          screen_id: screen.screen_id,
          data: normalizeFormRecord(fields, values),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`);

      const reset: Record<string, any> = {};
      for (const field of fields) reset[field.key] = "";
      setValues(reset);
      setCreated(true);
      useOsStore.getState().bumpScreenRefresh();
    } catch (err: any) {
      setError(err?.message || "Couldn't save record");
    } finally {
      setSubmitting(false);
    }
  };

  const handleValueChange = (key: string, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (created) setCreated(false);
  };

  return (
    <form onSubmit={handleSubmit} className="col-span-full bg-white rounded-2xl border border-slate-200 p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-800">Add {singular}</h3>
          <p className="mt-1 text-xs text-slate-500">Creates a live row in this workspace screen.</p>
        </div>
        {created && (
          <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            Saved
          </div>
        )}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {fields.map((field) => {
          const value = values[field.key] ?? "";
          const inputClass = "w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500";
          return (
            <div key={field.key}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">
                {field.label}
                {field.required && <span className="ml-1 text-red-500">*</span>}
              </label>
              {field.type === "date" ? (
                <input
                  type="date"
                  value={value}
                  onChange={(e) => handleValueChange(field.key, e.target.value)}
                  className={inputClass}
                />
              ) : field.type === "select" && Array.isArray((field as any).options) && (field as any).options.length > 0 ? (
                <select
                  value={value}
                  onChange={(e) => handleValueChange(field.key, e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select...</option>
                  {(field as any).options.map((option: string) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={(field.type as any) === "number" || field.type === "currency" ? "number" : "text"}
                  value={value}
                  onChange={(e) => handleValueChange(field.key, e.target.value)}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                  className={inputClass}
                />
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        style={{ backgroundColor: brandColor }}
        className="mt-5 inline-flex items-center justify-center rounded-xl px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? "Saving..." : "Save Record"}
      </button>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 5-A — types emitted by buildShapeSkeleton (claim-bundle.ts)
// ═══════════════════════════════════════════════════════════════════════

// LIST — flat list of records, click row → drawer, add records through the
// same live /api/screen-data modal used by tables and kanban boards.
function ListRenderer({ component, records, screen, workspaceId, schema }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema }) {
  const source = records ?? [];
  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0)
      ? screen.fields
      : (source.length > 0 ? inferFields(source[0]) : []);
  const primary = fields[0];
  const secondary = fields.slice(1, 3).filter(f => f.key !== "stage");
  const label = ((component.data_mapping as any)?.label as string | undefined) || screen.label;
  const screenNoun = screenCopyNoun(screen, "record");
  const singular = screenNoun.singular;
  const singularLabel = titleCaseWords(singular);
  // CRUD-1 — modal state + refresh trigger via the global os-store, so we
  // don't need a prop chain from SchemaRenderer down through ScreenContent.
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const onCreated = () => useOsStore.getState().bumpScreenRefresh();
  // DESIGN-1 — brand color for Add buttons + modal Create button.
  const brandColor = schema?.metadata?.brand_color;

  return (
    <>
      {source.length === 0 ? (
        fields.length > 0 ? (
          <StructuredRecordsEmptyState
            title={`Your ${singular} list is ready`}
            message={`Add your first ${singular} and it shows here.`}
            preview={<GhostListPreview fields={fields} />}
          >
            <button
              type="button"
              onClick={() => setBulkImportOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Upload className="w-4 h-4" /> Bring in existing {screenNoun.plural}
            </button>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              style={brandColor ? { backgroundColor: brandColor } : undefined}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium ${
                brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
              }`}
            >
              <Plus className="w-4 h-4" /> Add {singular}
            </button>
          </StructuredRecordsEmptyState>
        ) : (
          <EmptyState
            title={`Your ${singular} list is ready`}
            message={`Add your first ${singular} and it shows here.`}
            noun={singular}
          >
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setBulkImportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Upload className="w-4 h-4" /> Bring in existing {screenNoun.plural}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                style={brandColor ? { backgroundColor: brandColor } : undefined}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium ${
                  brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
                }`}
              >
                <Plus className="w-4 h-4" /> Add {singular}
              </button>
            </div>
          </EmptyState>
        )
      ) : (
        <div className="col-span-full bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">{label}</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBulkImportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Upload className="w-3.5 h-3.5" /> Import
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                style={brandColor ? { backgroundColor: brandColor } : undefined}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold ${
                  brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
                }`}
              >
                <Plus className="w-3.5 h-3.5" /> Add {singular}
              </button>
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {source.map((record: any) => (
              <li
                key={record.id ?? String(record[primary?.key ?? "name"])}
                onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
                className="px-6 py-4 hover:bg-slate-50/50 transition-colors cursor-pointer group flex items-center gap-4"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                  {String(record[primary?.key ?? "name"] ?? "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                    {String(record[primary?.key ?? "name"] ?? "—")}
                  </div>
                  {secondary.length > 0 && (
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {secondary.map(f => String(record[f.key] ?? "")).filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 transition-colors shrink-0" />
              </li>
            ))}
          </ul>
        </div>
      )}
      <CreateRecordModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={onCreated}
        workspaceId={workspaceId}
        screenId={screen.screen_id}
        fields={fields}
        title={`New ${singularLabel}`}
        existingRecords={source}
        brandColor={brandColor}
      />
      <BulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImported={onCreated}
        workspaceId={workspaceId}
        screenId={screen.screen_id}
        screenLabel={screen.label}
        noun={screenNoun}
        fields={fields}
        brandColor={brandColor}
      />
    </>
  );
}

// FILTER — pill row of distinct values for one field. When ScreenContent
// provides controlled state, selection filters the visible list/board below.
function FilterRenderer({
  component,
  records,
  filterState,
  onFilterChange,
}: {
  component: UIComponent;
  records?: any[];
  filterState?: { field: string; value: string | null } | null;
  onFilterChange?: (field: string, value: string | null) => void;
}) {
  const field = ((component.data_mapping as any)?.field as string | undefined) || "stage";
  const label = ((component.data_mapping as any)?.label as string | undefined) || `Filter by ${field}`;
  const [localSelected, setLocalSelected] = useState<string | null>(null);
  const source = records ?? [];
  const distinct = useMemo(
    () => Array.from(new Set(source.map((r: any) => r?.[field]).filter(Boolean))) as string[],
    [source, field],
  );
  const selected = filterState?.field === field ? filterState.value : localSelected;
  const setSelected = (value: string | null) => {
    setLocalSelected(value);
    onFilterChange?.(field, value);
  };

  if (source.length === 0) {
    return (
      <div className="col-span-full text-xs text-slate-400 italic px-1">
        Filters appear once records are added.
      </div>
    );
  }

  const pillBase = "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border";
  const pillIdle = "bg-white border-slate-200 text-slate-600 hover:bg-slate-50";
  const pillActive = "bg-slate-900 border-slate-900 text-white";

  return (
    <div className="col-span-full">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className={`${pillBase} ${selected === null ? pillActive : pillIdle}`}
        >
          All
        </button>
        {distinct.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setSelected(value)}
            className={`${pillBase} ${selected === value ? pillActive : pillIdle}`}
          >
            {value}
          </button>
        ))}
      </div>
    </div>
  );
}

// SEARCH — input over one field. When ScreenContent provides controlled state,
// query filters the visible list/board below.
function SearchRenderer({
  component,
  searchState,
  onSearchChange,
}: {
  component: UIComponent;
  searchState?: { field: string; query: string } | null;
  onSearchChange?: (field: string, query: string) => void;
}) {
  const field = ((component.data_mapping as any)?.field as string | undefined) || "name";
  const [localQuery, setLocalQuery] = useState("");
  const query = searchState?.field === field ? searchState.query : localQuery;
  const setQuery = (value: string) => {
    setLocalQuery(value);
    onSearchChange?.(field, value);
  };
  return (
    <div className="col-span-full">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search by ${field}...`}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
        />
      </div>
    </div>
  );
}

// STAT ROW — horizontal strip of counts grouped by stage (or whichever
// distinct field makes sense from records). Used for "Stage breakdown".
function StatRowRenderer({ component, records, screen }: { component: UIComponent; records?: any[]; screen?: ScreenTemplate }) {
  const label = ((component.data_mapping as any)?.label as string | undefined) || "Breakdown";
  const groupBy = ((component.data_mapping as any)?.group_by as string | undefined) || "stage";
  const source = records ?? [];
  const noun = screenCopyNoun(screen, "record").singular;

  if (source.length === 0) {
    return (
      <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center">
        <p className="text-xs text-slate-500">
          {`Your ${noun} summary is ready. Once you add your first ${noun}, the totals appear here.`}
        </p>
      </div>
    );
  }

  const counts = source.reduce<Record<string, number>>((acc, r: any) => {
    const key = String(r?.[groupBy] ?? "Unspecified");
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  const entries = Object.entries(counts);

  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-4">{label}</h3>
      <div className="flex flex-wrap gap-3">
        {entries.map(([stage, count]) => (
          <div key={stage} className="flex-1 min-w-[140px] rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
            <div className="text-xs font-medium text-slate-500 truncate">{stage}</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// BRAND PANEL — Agentic Operating System-level settings summary with a single truthful action.
function BrandPanelRenderer({ schema, onManage }: { schema: OSSchema; onManage?: () => void }) {
  const name = schema.metadata.name || "Untitled Agentic Operating System";
  const color = schema.metadata.brand_color || DEFAULT_BRAND_COLOR;
  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold text-slate-800">Agentic Operating System settings</h3>
          <p className="text-xs text-slate-500 mt-1">Identity, workspace access, and support controls live in Settings.</p>
        </div>
        {onManage && (
          <button
            type="button"
            onClick={onManage}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 shrink-0"
          >
            Open Settings <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4 py-3 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Agentic Operating System name</div>
            <div className="text-sm font-medium text-slate-900 truncate mt-1">{name}</div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 py-3">
          <div className="min-w-0 flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg border border-slate-200 shrink-0"
              style={{ backgroundColor: color }}
              aria-label={`Brand color ${color}`}
            />
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Brand color</div>
              <div className="text-sm font-mono text-slate-900 mt-1">{color.toUpperCase()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PHASE 5-D — industry-shape-aware grid renderers
// ═══════════════════════════════════════════════════════════════════════

// Small helper used by the 4 new renderers — return a "performance tone"
// for a row based on its status/state value. Best-effort string match
// across shape-appropriate vocab (sales/legal/manufacturing/healthcare/
// design/portfolio). Unknown values land on neutral gray. English-only —
// localization is a P3 follow-up.
function performanceTone(v: any): "positive" | "negative" | "neutral" {
  const s = String(v ?? "").toLowerCase();
  if (/won|complete|completed|active|live|closed|delivered|shipped|deployed|approved|confirmed|in_progress|ongoing|ok|good|up|green|on_track|healthy/.test(s)) return "positive";
  if (/lost|stalled|blocked|overdue|defect|fail|failed|cancelled|canceled|delayed|behind|paused|escalated|at_risk|red|down|critical/.test(s)) return "negative";
  return "neutral";
}

// Empty-state card used by all 4 new renderers — DRY follow-up.
function GridEmptyState({ icon, label, hint, children }: { icon: ReactNode; label: string; hint?: string; children?: ReactNode }) {
  return (
    <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-white p-10 flex flex-col items-center justify-center text-center">
      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3 text-slate-400">
        {icon}
      </div>
      <p className="text-sm font-medium text-slate-900">{label}</p>
      {hint && <p className="text-xs text-slate-500 mt-1 max-w-xs">{hint}</p>}
      {children}
    </div>
  );
}

function EmptyGridActions({
  noun,
  brandColor,
  canAdd,
  onAdd,
  onConnect,
}: {
  noun: string;
  brandColor?: string;
  canAdd: boolean;
  onAdd: () => void;
  onConnect?: () => void;
}) {
  return (
    <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
      {canAdd && (
        <button
          type="button"
          onClick={onAdd}
          style={brandColor ? { backgroundColor: brandColor } : undefined}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium ${
            brandColor ? "hover:opacity-90" : "bg-zinc-900 hover:bg-zinc-800"
          }`}
        >
          <Plus className="w-4 h-4" /> Add first {noun.toLowerCase()}
        </button>
      )}
      {onConnect && (
        <button
          type="button"
          onClick={onConnect}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <Plug className="w-4 h-4" /> Connect data
        </button>
      )}
    </div>
  );
}

// PORTFOLIO — grid of mandate/investment cards. Each card carries a left
// accent bar tinted by status (green = active, red = stalled). Designed
// for family-office / asset-management shapes.
function PortfolioCardRenderer({ component, records, screen, workspaceId, schema, onNavigateScreen }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema; onNavigateScreen?: (screenId: string) => void }) {
  const label = ((component.data_mapping as any)?.label as string | undefined) || `${screen.label} portfolio`;
  const source = records ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const brandColor = schema?.metadata?.brand_color;
  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : (source.length > 0 ? inferFields(source[0]) : []);
  const screenNoun = screenCopyNoun(screen, "record");
  const singular = titleCaseWords(screenNoun.singular);
  const noun = screenNoun.singular;
  if (source.length === 0) {
    return (
      <>
        <GridEmptyState icon={<Briefcase className="w-5 h-5" />} label={`Your ${noun} board is ready`} hint={`Add your first ${noun} and it appears here.`}>
          <EmptyGridActions
            noun={singular}
            brandColor={brandColor}
            canAdd={fields.length > 0}
            onAdd={() => setModalOpen(true)}
            onConnect={onNavigateScreen ? () => onNavigateScreen("integrations") : undefined}
          />
        </GridEmptyState>
        <CreateRecordModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={() => useOsStore.getState().bumpScreenRefresh()}
          workspaceId={workspaceId}
          screenId={screen.screen_id}
          fields={fields}
          title={`New ${singular}`}
          existingRecords={source}
          brandColor={brandColor}
        />
      </>
    );
  }
  const nameField = fields.find(f => /name|mandate/i.test(f.key)) || fields[0];
  const primaryField = fields.find(f => f.type === "currency" || /capital|value|aum/i.test(f.key)) || fields[1];
  const secondaryField = fields.find(f => /status|state|stage/i.test(f.key)) || fields.find(f => f.key !== nameField?.key && f.key !== primaryField?.key);
  const ownerField = fields.find(f => /principal|owner|family/i.test(f.key));
  return (
    <div className="col-span-full">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">{label}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {source.map((record: any) => {
          const tone = performanceTone(record[secondaryField?.key ?? "status"]);
          const accent =
            tone === "positive" ? "bg-emerald-400" :
            tone === "negative" ? "bg-rose-400" :
            "bg-slate-300";
          return (
            <div
              key={record.id ?? String(record[nameField?.key ?? "name"])}
              onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
              className="relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-all cursor-pointer group"
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
              <div className="p-5 pl-6">
                <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                  {String(record[nameField?.key ?? "name"] ?? "—")}
                </div>
                {primaryField && (
                  <div className="text-2xl font-bold text-slate-900 mt-2">
                    {renderField(primaryField, record)}
                  </div>
                )}
                <div className="flex items-center justify-between mt-3 gap-2">
                  {secondaryField && (
                    <span className="text-xs text-slate-600 truncate">
                      {String(record[secondaryField.key] ?? "—")}
                    </span>
                  )}
                  {ownerField && (
                    <span className="text-xs text-slate-500 shrink-0">
                      {String(record[ownerField.key] ?? "")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// GALLERY — visual grid of project cards with thumbnail-style first-letter
// monogram, status badge, and client line. Designed for editorial / design
// / agency / media shapes.
function GalleryCardRenderer({ component, records, screen, workspaceId, schema, onNavigateScreen }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema; onNavigateScreen?: (screenId: string) => void }) {
  const label = ((component.data_mapping as any)?.label as string | undefined) || `${screen.label} gallery`;
  const source = records ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const brandColor = schema?.metadata?.brand_color;
  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : (source.length > 0 ? inferFields(source[0]) : []);
  const screenNoun = screenCopyNoun(screen, "record");
  const singular = titleCaseWords(screenNoun.singular);
  const noun = screenNoun.singular;
  if (source.length === 0) {
    return (
      <>
        <GridEmptyState icon={<ImageIcon className="w-5 h-5" />} label={`Your ${noun} gallery is ready`} hint={`Add your first ${noun} and it appears here.`}>
          <EmptyGridActions
            noun={singular}
            brandColor={brandColor}
            canAdd={fields.length > 0}
            onAdd={() => setModalOpen(true)}
            onConnect={onNavigateScreen ? () => onNavigateScreen("integrations") : undefined}
          />
        </GridEmptyState>
        <CreateRecordModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={() => useOsStore.getState().bumpScreenRefresh()}
          workspaceId={workspaceId}
          screenId={screen.screen_id}
          fields={fields}
          title={`New ${singular}`}
          existingRecords={source}
          brandColor={brandColor}
        />
      </>
    );
  }
  const nameField = fields.find(f => /name|title|project/i.test(f.key)) || fields[0];
  const clientField = fields.find(f => /client|customer|account/i.test(f.key));
  const statusField = fields.find(f => /status|state|stage/i.test(f.key));
  return (
    <div className="col-span-full">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">{label}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {source.map((record: any) => {
          const name = String(record[nameField?.key ?? "name"] ?? "—");
          const tone = performanceTone(record[statusField?.key ?? "status"]);
          const badge =
            tone === "positive" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
            tone === "negative" ? "bg-rose-50 text-rose-700 border-rose-200" :
            "bg-slate-50 text-slate-600 border-slate-200";
          return (
            <div
              key={record.id ?? name}
              onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
            >
              <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <span className="text-5xl font-bold text-slate-400">{name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="p-4">
                <div className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                  {name}
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  {clientField && (
                    <span className="text-xs text-slate-500 truncate">
                      {String(record[clientField.key] ?? "")}
                    </span>
                  )}
                  {statusField && (
                    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${badge}`}>
                      {String(record[statusField.key] ?? "—")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// SCHEDULE — 7-day column view. Records with a date field bucket into the
// next 7 days; records without a date field fall back to a flat list under
// "Unscheduled". Designed for health / hospitality / education shapes.
function ScheduleBlockRenderer({ component, records, screen, workspaceId, schema, onNavigateScreen }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema; onNavigateScreen?: (screenId: string) => void }) {
  const label = ((component.data_mapping as any)?.label as string | undefined) || `${screen.label} schedule`;
  const source = records ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const brandColor = schema?.metadata?.brand_color;
  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : (source.length > 0 ? inferFields(source[0]) : []);
  const screenNoun = screenCopyNoun(screen, "record");
  const singular = titleCaseWords(screenNoun.singular);
  const noun = screenNoun.singular;
  if (source.length === 0) {
    return (
      <>
        <GridEmptyState icon={<CalendarDays className="w-5 h-5" />} label={`Your ${noun} schedule is ready`} hint={`Add your first ${noun} and it appears here.`}>
          <EmptyGridActions
            noun={singular}
            brandColor={brandColor}
            canAdd={fields.length > 0}
            onAdd={() => setModalOpen(true)}
            onConnect={onNavigateScreen ? () => onNavigateScreen("integrations") : undefined}
          />
        </GridEmptyState>
        <CreateRecordModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={() => useOsStore.getState().bumpScreenRefresh()}
          workspaceId={workspaceId}
          screenId={screen.screen_id}
          fields={fields}
          title={`New ${singular}`}
          existingRecords={source}
          brandColor={brandColor}
        />
      </>
    );
  }
  const dateField = fields.find(f => f.type === "date") || fields.find(f => /date|time|when|scheduled/i.test(f.key));
  const nameField = fields.find(f => /name|patient|guest|student|client/i.test(f.key)) || fields[0];
  const ownerField = fields.find(f => /physician|provider|trainer|host|room|location/i.test(f.key));
  const durationField = fields.find(f => /duration|length|minutes/i.test(f.key));

  // Build 7-day column buckets keyed by YYYY-MM-DD. Records without a
  // parseable date land in a final "Unscheduled" column.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days: { key: string; label: string; dayLabel: string; items: any[] }[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today.getTime() + i * 86400000);
    const key = d.toISOString().slice(0, 10);
    days.push({
      key,
      label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      dayLabel: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString("en-US", { weekday: "short" }),
      items: [],
    });
  }
  const unscheduled: any[] = [];
  for (const rec of source) {
    const raw = dateField ? rec[dateField.key] : null;
    const parsed = raw ? new Date(raw) : null;
    if (!parsed || isNaN(parsed.getTime())) { unscheduled.push(rec); continue; }
    const key = parsed.toISOString().slice(0, 10);
    const bucket = days.find(d => d.key === key);
    if (bucket) bucket.items.push(rec); else unscheduled.push(rec);
  }

  return (
    <div className="col-span-full">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">{label}</h3>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {days.map(d => (
          <div key={d.key} className="min-w-[180px] flex-shrink-0 bg-slate-50 rounded-2xl p-3 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-slate-700">{d.dayLabel}</div>
              <span className="text-[10px] font-bold text-slate-400 bg-white px-1.5 py-0.5 rounded-full">{d.items.length}</span>
            </div>
            <div className="text-[10px] text-slate-500 mb-3">{d.label}</div>
            <div className="flex flex-col gap-2 flex-1">
              {d.items.length === 0 ? (
                <div className="text-[11px] text-slate-400 italic text-center py-3">No bookings</div>
              ) : (
                d.items.map(rec => (
                  <div
                    key={rec.id ?? `${d.key}-${rec[nameField?.key ?? "name"]}`}
                    onClick={() => useOsStore.getState().openDrawer(rec, screen, workspaceId)}
                    className="bg-white rounded-xl border border-slate-200 p-2.5 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <div className="text-xs font-medium text-slate-900 truncate">
                      {String(rec[nameField?.key ?? "name"] ?? "—")}
                    </div>
                    {ownerField && (
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                        {String(rec[ownerField.key] ?? "")}
                      </div>
                    )}
                    {durationField && (
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {String(rec[durationField.key] ?? "")}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
        {unscheduled.length > 0 && (
          <div className="min-w-[180px] flex-shrink-0 bg-amber-50 rounded-2xl p-3 flex flex-col border border-amber-100">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-semibold text-amber-800">Unscheduled</div>
              <span className="text-[10px] font-bold text-amber-700 bg-white px-1.5 py-0.5 rounded-full">{unscheduled.length}</span>
            </div>
            <div className="flex flex-col gap-2 flex-1">
              {unscheduled.map(rec => (
                <div
                  key={rec.id ?? `un-${rec[nameField?.key ?? "name"]}`}
                  onClick={() => useOsStore.getState().openDrawer(rec, screen, workspaceId)}
                  className="bg-white rounded-xl border border-amber-200 p-2.5 hover:shadow-sm transition-all cursor-pointer"
                >
                  <div className="text-xs font-medium text-slate-900 truncate">
                    {String(rec[nameField?.key ?? "name"] ?? "—")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// PRODUCTION — grid of run cards with progress bar (derived from
// current/target fields when present). Designed for manufacturing /
// production / fulfillment shapes.
function ProductionRunRenderer({ component, records, screen, workspaceId, schema, onNavigateScreen }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema; onNavigateScreen?: (screenId: string) => void }) {
  const label = ((component.data_mapping as any)?.label as string | undefined) || `${screen.label} runs`;
  const source = records ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const brandColor = schema?.metadata?.brand_color;
  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : (source.length > 0 ? inferFields(source[0]) : []);
  const screenNoun = screenCopyNoun(screen, "record");
  const singular = titleCaseWords(screenNoun.singular);
  const noun = screenNoun.singular;
  if (source.length === 0) {
    return (
      <>
        <GridEmptyState icon={<Package className="w-5 h-5" />} label={`Your ${noun} board is ready`} hint={`Add your first ${noun} and it appears here.`}>
          <EmptyGridActions
            noun={singular}
            brandColor={brandColor}
            canAdd={fields.length > 0}
            onAdd={() => setModalOpen(true)}
            onConnect={onNavigateScreen ? () => onNavigateScreen("integrations") : undefined}
          />
        </GridEmptyState>
        <CreateRecordModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onCreated={() => useOsStore.getState().bumpScreenRefresh()}
          workspaceId={workspaceId}
          screenId={screen.screen_id}
          fields={fields}
          title={`New ${singular}`}
          existingRecords={source}
          brandColor={brandColor}
        />
      </>
    );
  }
  const nameField = fields.find(f => /name|batch|run|order/i.test(f.key)) || fields[0];
  const throughputField = fields.find(f => /throughput|produced|count|units/i.test(f.key));
  const statusField = fields.find(f => /status|state|stage/i.test(f.key));
  const stationField = fields.find(f => /station|line|owner|operator/i.test(f.key));
  const currentField = fields.find(f => /current|done|complete/i.test(f.key));
  const targetField = fields.find(f => /target|goal|total/i.test(f.key));

  return (
    <div className="col-span-full">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">{label}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {source.map((record: any) => {
          const tone = performanceTone(record[statusField?.key ?? "status"]);
          const bar =
            tone === "positive" ? "bg-emerald-500" :
            tone === "negative" ? "bg-rose-500" :
            "bg-indigo-500";
          const current = currentField ? Number(record[currentField.key]) : NaN;
          const target = targetField ? Number(record[targetField.key]) : NaN;
          const pct = Number.isFinite(current) && Number.isFinite(target) && target > 0
            ? Math.max(0, Math.min(100, Math.round((current / target) * 100)))
            : null;
          return (
            <div
              key={record.id ?? String(record[nameField?.key ?? "name"])}
              onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
              className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                  {String(record[nameField?.key ?? "name"] ?? "—")}
                </div>
                {statusField && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">
                    {String(record[statusField.key] ?? "")}
                  </span>
                )}
              </div>
              {throughputField && (
                <div className="text-2xl font-bold text-slate-900 mb-3">
                  {String(record[throughputField.key] ?? "—")}
                </div>
              )}
              {pct !== null && (
                <div className="mb-2">
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">{pct}% complete</div>
                </div>
              )}
              {stationField && (
                <div className="text-xs text-slate-600 truncate">
                  {String(record[stationField.key] ?? "")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// R3 — URGENT ACTIONS — ranked alerts with a drafted next action. The
// intelligence card the solution-shape patterns prescribe (Analyst's top-3
// ranked issues, Domain Tracker's smart alerts). Items come from REAL
// records only via data_mapping; honest empty state when nothing needs
// attention — never fabricated (R1 discipline, no randomized values). The action
// button OPENS the record (openDrawer) — it does NOT imply auto-send.
//
// data_mapping contract (all optional):
//   label        — card heading (fallback: "Needs your attention")
//   title        — record field key for the action title (fallback: first field)
//   context      — record field key for the one-line why-now
//   detail       — record field key for a secondary detail line
//   urgency      — record field key whose value ranks the item (high/med/low)
//   action_label — record field key holding the drafted-action button label
function UrgentActionsRenderer({ component, records, screen, workspaceId, schema }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema }) {
  const dm = (component.data_mapping || {}) as Record<string, string>;
  const label = dm.label || "Needs your attention";
  const brandColor = schema?.metadata?.brand_color || DEFAULT_BRAND_COLOR;
  const source = records ?? [];

  // R1 — honest empty state. Never fabricate action items.
  if (source.length === 0) {
    return (
      <GridEmptyState
        icon={<Bell className="w-5 h-5" />}
        label="No actions need attention right now"
        hint="As your data grows, the items that need a decision will surface here."
      />
    );
  }

  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : inferFields(source[0]);
  const titleKey = dm.title || fields[0]?.key || "name";
  const contextKey = dm.context;
  const detailKey = dm.detail;
  const urgencyKey = dm.urgency;
  const actionKey = dm.action_label;

  // Deterministic urgency rank (no randomness). Unknown values sort last;
  // Array.sort is stable so original order is preserved within a tier.
  const urgencyRank = (v: any): number => {
    const s = String(v ?? "").toLowerCase();
    if (/high|urgent|critical|overdue|now|today|p0|p1/.test(s)) return 0;
    if (/medium|med|normal|soon|p2/.test(s)) return 1;
    if (/low|later|p3/.test(s)) return 2;
    return 3;
  };
  const items = urgencyKey
    ? source
        .map((r, i) => ({ r, i }))
        .sort((a, b) => {
          const d = urgencyRank(a.r[urgencyKey]) - urgencyRank(b.r[urgencyKey]);
          return d !== 0 ? d : a.i - b.i;
        })
        .map((x) => x.r)
    : source;

  // Marker intensity derives from the REAL urgency value (not invented).
  // No urgency field mapped → all markers full brand.
  const markerOpacity = (v: any): number => {
    if (!urgencyKey) return 1;
    switch (urgencyRank(v)) {
      case 0: return 1;
      case 1: return 0.55;
      case 2: return 0.3;
      default: return 0.55;
    }
  };

  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
        <Bell className="w-4 h-4 text-slate-500" />
        <h3 className="font-semibold text-slate-800">{label}</h3>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((record: any, idx: number) => {
          const title = String(record[titleKey] ?? "—");
          const context = contextKey ? record[contextKey] : null;
          const detail = detailKey ? record[detailKey] : null;
          const actionLabel = actionKey ? record[actionKey] : null;
          return (
            <li
              key={record.id ?? `${title}-${idx}`}
              onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
              className="px-6 py-4 hover:bg-slate-50/50 transition-colors cursor-pointer group flex items-start gap-3"
            >
              <span
                className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: brandColor, opacity: markerOpacity(record[urgencyKey ?? ""]) }}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                  {title}
                </div>
                {context != null && String(context).trim() !== "" && (
                  <div className="text-xs text-slate-600 mt-0.5">{String(context)}</div>
                )}
                {detail != null && String(detail).trim() !== "" && (
                  <div className="text-xs text-slate-400 mt-0.5">{String(detail)}</div>
                )}
              </div>
              {actionLabel != null && String(actionLabel).trim() !== "" && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); useOsStore.getState().openDrawer(record, screen, workspaceId); }}
                  style={{ borderColor: brandColor, color: brandColor }}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-medium border rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors"
                >
                  {String(actionLabel)} <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// R3 #2 — SCORED BUCKETS — the Qualifier pattern's verdict card. Turns inbound
// items into DECISIONS using NAMED BUCKETS (HOT/WARM/COLD/SKIP), never a 1-100
// number. Scores FIT and INTENT separately (they fail differently: great-fit/
// zero-intent = nurture; high-intent/poor-fit = polite no), shows two-sided
// reasoning (positives + concerns), and ends with a drafted action. Real
// records only; honest empty state; NEVER invents a bucket for an unscored
// record — unknown stays neutral (R1 discipline, no randomness). The action
// button OPENS the record (openDrawer) — it does NOT imply auto-send.
//
// data_mapping contract (all optional):
//   label        — card heading (fallback: "Qualification")
//   title        — record field key for the item subject (fallback: first field)
//   bucket       — record field key for the OVERALL named verdict
//   fit          — record field key for the FIT sub-verdict
//   intent       — record field key for the INTENT sub-verdict
//   positives    — record field key (string or array) rendered as a ✓ list
//   concerns     — record field key (string or array) rendered as a ⚠ list
//   action_label — record field key holding the drafted-action button label
type BucketTier = "hot" | "warm" | "cold" | "skip" | "unknown";

function bucketTier(v: any): BucketTier {
  const s = String(v ?? "").toLowerCase();
  if (!s.trim()) return "unknown";
  if (/\bhot\b|priority|qualified|act now|p0|p1/.test(s)) return "hot";
  if (/\bwarm\b|medium|maybe|nurture|p2/.test(s)) return "warm";
  if (/\bcold\b|\blow\b|later|p3/.test(s)) return "cold";
  if (/\bskip\b|disqualif|reject|drop|pass|no fit/.test(s)) return "skip";
  return "unknown";
}

// Bucket colors are SEMANTIC and fixed (not brand-tinted): triage reads on
// color. Icon by tier; color-coded regardless so an unknown bucket still reads.
const BUCKET_STYLE: Record<BucketTier, { badge: string; icon: ReactNode; rank: number }> = {
  hot:     { badge: "bg-red-50 text-red-700 border-red-200",        icon: <Flame className="w-3.5 h-3.5" />,          rank: 0 },
  warm:    { badge: "bg-amber-50 text-amber-700 border-amber-200",  icon: <ThermometerSun className="w-3.5 h-3.5" />, rank: 1 },
  cold:    { badge: "bg-blue-50 text-blue-700 border-blue-200",     icon: <Snowflake className="w-3.5 h-3.5" />,      rank: 2 },
  skip:    { badge: "bg-slate-100 text-slate-500 border-slate-200", icon: <CircleSlash className="w-3.5 h-3.5" />,    rank: 3 },
  unknown: { badge: "bg-slate-50 text-slate-400 border-slate-200",  icon: <Circle className="w-3.5 h-3.5" />,         rank: 4 },
};

function BucketBadge({ value, prominent }: { value: any; prominent?: boolean }) {
  const tier = bucketTier(value);
  const style = BUCKET_STYLE[tier];
  const text = String(value ?? "").trim();
  // Never guess a bucket: an unmapped/empty value shows neutral, never HOT/COLD.
  const display = text ? text.toUpperCase() : "—";
  return (
    <span
      className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider rounded-full border ${style.badge} ${
        prominent ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5"
      }`}
    >
      {style.icon}
      {display}
    </span>
  );
}

function toList(v: any): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x)).filter((s) => s.trim() !== "");
  if (typeof v === "string" && v.trim() !== "") return [v.trim()];
  return [];
}

function ScoredBucketsRenderer({ component, records, screen, workspaceId, schema }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema }) {
  const dm = (component.data_mapping || {}) as Record<string, string>;
  const label = dm.label || "Qualification";
  const brandColor = schema?.metadata?.brand_color || DEFAULT_BRAND_COLOR;
  const source = records ?? [];

  // R1 — honest empty state. Never fabricate verdicts.
  if (source.length === 0) {
    return (
      <GridEmptyState
        icon={<ScanSearch className="w-5 h-5" />}
        label="No items to qualify yet"
        hint="As inbound items arrive, their fit, intent, and verdict will surface here."
      />
    );
  }

  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : inferFields(source[0]);
  const titleKey = dm.title || fields[0]?.key || "name";
  const bucketKey = dm.bucket;
  const fitKey = dm.fit;
  const intentKey = dm.intent;
  const positivesKey = dm.positives;
  const concernsKey = dm.concerns;
  const actionKey = dm.action_label;

  // Triage order: HOT → WARM → COLD → SKIP → unknown. Deterministic, stable
  // (Array.sort), sorted on the OVERALL bucket field. No bucket mapped →
  // preserve incoming order.
  const items = bucketKey
    ? source
        .map((r, i) => ({ r, i }))
        .sort((a, b) => {
          const d = BUCKET_STYLE[bucketTier(a.r[bucketKey])].rank - BUCKET_STYLE[bucketTier(b.r[bucketKey])].rank;
          return d !== 0 ? d : a.i - b.i;
        })
        .map((x) => x.r)
    : source;

  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
        <ScanSearch className="w-4 h-4 text-slate-500" />
        <h3 className="font-semibold text-slate-800">{label}</h3>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((record: any, idx: number) => {
          const title = String(record[titleKey] ?? "—");
          const positives = positivesKey ? toList(record[positivesKey]) : [];
          const concerns = concernsKey ? toList(record[concernsKey]) : [];
          const hasFit = fitKey && String(record[fitKey] ?? "").trim() !== "";
          const hasIntent = intentKey && String(record[intentKey] ?? "").trim() !== "";
          return (
            <li
              key={record.id ?? `${title}-${idx}`}
              onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
              className="px-6 py-4 hover:bg-slate-50/50 transition-colors cursor-pointer group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                    {title}
                  </div>
                  {(hasFit || hasIntent) && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                      {hasFit && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">Fit</span>
                          <BucketBadge value={record[fitKey!]} />
                        </div>
                      )}
                      {hasIntent && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">Intent</span>
                          <BucketBadge value={record[intentKey!]} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {bucketKey && <BucketBadge value={record[bucketKey]} prominent />}
              </div>

              {(positives.length > 0 || concerns.length > 0) && (
                <div className="flex flex-col sm:flex-row gap-x-6 gap-y-2 mt-3">
                  {positives.length > 0 && (
                    <ul className="flex-1 space-y-1">
                      {positives.map((p, i) => (
                        <li key={`p-${i}`} className="flex items-start gap-1.5 text-xs text-slate-600">
                          <span className="text-emerald-500 shrink-0 mt-px">✓</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {concerns.length > 0 && (
                    <ul className="flex-1 space-y-1">
                      {concerns.map((c, i) => (
                        <li key={`c-${i}`} className="flex items-start gap-1.5 text-xs text-slate-600">
                          <span className="text-amber-500 shrink-0 mt-px">⚠</span>
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {actionKey && String(record[actionKey] ?? "").trim() !== "" && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); useOsStore.getState().openDrawer(record, screen, workspaceId); }}
                  style={{ borderColor: brandColor, color: brandColor }}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium border rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors"
                >
                  {String(record[actionKey])} <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// R3 #3 — DEVELOPMENT TIMELINE — the Domain Tracker's per-entity progression
// view. Each entity (person/asset/account) carries a development TIMELINE
// (ordered periods, each a note + optional score) — progression, not a
// snapshot — plus an honest confidence/verification flag. Renders from REAL
// records only; an entity with no timeline shows an honest per-entity "no
// history" line; the confidence indicator appears ONLY when the field has a
// real value — never implies certainty the data doesn't support (the Domain
// Tracker's core honesty rule). Scores render ONLY when a real number exists —
// never 0, never a guess (R1 discipline, no randomness). The row opens the
// entity record (openDrawer) — no implied action beyond that.
//
// data_mapping contract (all optional):
//   label      — card heading (fallback: "Development")
//   title      — record field key for the entity name (fallback: first field)
//   timeline   — record field key whose value is Array<{ period, note, score? }>
//   confidence — record field key for the confidence/verification value
interface TimelineEntry { period?: any; note?: any; score?: any }

function isVerified(v: any): boolean {
  return /verified|confirmed|complete|trusted|\bhigh\b/i.test(String(v ?? ""));
}

function DevelopmentTimelineRenderer({ component, records, screen, workspaceId, schema }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema }) {
  const dm = (component.data_mapping || {}) as Record<string, string>;
  const label = dm.label || "Development";
  const brandColor = schema?.metadata?.brand_color || DEFAULT_BRAND_COLOR;
  const source = records ?? [];

  // R1 — honest empty state. Never fabricate entities or history.
  if (source.length === 0) {
    return (
      <GridEmptyState
        icon={<History className="w-5 h-5" />}
        label="No history tracked yet"
        hint="As each entity develops, its timeline and verification status will appear here."
      />
    );
  }

  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : inferFields(source[0]);
  const titleKey = dm.title || fields[0]?.key || "name";
  const timelineKey = dm.timeline;
  const confidenceKey = dm.confidence;

  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
        <History className="w-4 h-4 text-slate-500" />
        <h3 className="font-semibold text-slate-800">{label}</h3>
      </div>
      <ul className="divide-y divide-slate-100">
        {source.map((record: any, idx: number) => {
          const title = String(record[titleKey] ?? "—");
          const rawTimeline = timelineKey ? record[timelineKey] : null;
          const timeline: TimelineEntry[] = Array.isArray(rawTimeline) ? rawTimeline : [];
          const confidenceVal = confidenceKey ? record[confidenceKey] : null;
          const hasConfidence = confidenceVal != null && String(confidenceVal).trim() !== "";
          const verified = hasConfidence && isVerified(confidenceVal);
          return (
            <li
              key={record.id ?? `${title}-${idx}`}
              onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
              className="px-6 py-4 hover:bg-slate-50/50 transition-colors cursor-pointer group"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                  {title}
                </div>
                {/* Confidence/verification — shown ONLY when a real value exists.
                    Absent → nothing; never imply "verified" the data lacks. */}
                {hasConfidence && (
                  verified ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 shrink-0">
                      <BadgeCheck className="w-3.5 h-3.5" /> {String(confidenceVal)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 shrink-0">
                      {String(confidenceVal)}
                    </span>
                  )
                )}
              </div>

              {timeline.length > 0 ? (
                <ol className="mt-3 space-y-2">
                  {timeline.map((entry, i) => {
                    const period = entry?.period != null ? String(entry.period) : "";
                    const note = entry?.note != null ? String(entry.note) : "";
                    const hasScore = typeof entry?.score === "number" && Number.isFinite(entry.score);
                    return (
                      <li key={i} className="flex items-start gap-2">
                        <CircleDot className="w-3 h-3 mt-0.5 shrink-0" style={{ color: brandColor }} aria-hidden="true" />
                        <div className="text-xs flex-1 min-w-0">
                          {period && <span className="font-medium text-slate-700">{period}</span>}
                          {period && note && <span className="text-slate-400"> · </span>}
                          {note && <span className="text-slate-600">{note}</span>}
                          {hasScore && <span className="text-slate-500"> — {entry.score}</span>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="mt-2 text-xs text-slate-400">No history tracked yet</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// R3 #4 — SPECIALIST STATUS — the Router pattern's persistent menu of
// specialists. Renders a roster of cards from REAL records — each: name, a
// color-coded SEMANTIC status dot (active=green, idle=amber, offline=gray),
// and a one-line coverage description. The left accent bar = identity (per-card
// color or brand); the status dot = state — never conflated. Status comes from
// the real field; unknown/absent → neutral dot + NO label, never a guessed
// "active" (R1 discipline, no randomness). The card opens the record
// (openDrawer). (RoutingBrief / UsageGuide are per-request AI outputs, not this
// persistent roster.)
//
// data_mapping contract (all optional):
//   label       — card heading (fallback: "Specialists")
//   name        — record field key for the specialist name (fallback: first field)
//   status      — record field key for the status value (active/idle/offline)
//   description — record field key for the one-line coverage description
//   color       — record field key for a per-card accent color (hex); else brand
type SpecialistState = "active" | "idle" | "offline" | "unknown";

function specialistState(v: any): SpecialistState {
  const s = String(v ?? "").toLowerCase();
  if (!s.trim()) return "unknown";
  // Word boundaries so "inactive"/"unavailable" don't false-match active.
  if (/\bactive\b|\bonline\b|\bavailable\b|\bon\b|\blive\b|\bbusy\b/.test(s)) return "active";
  if (/\bidle\b|\baway\b|\bstandby\b|\bpaused\b/.test(s)) return "idle";
  if (/\boffline\b|\binactive\b|\bunavailable\b|\boff\b|\bout\b/.test(s)) return "offline";
  return "unknown";
}

// Semantic status dot — fixed colors, independent of the brand accent.
const SPECIALIST_DOT: Record<SpecialistState, string> = {
  active:  "bg-emerald-500",
  idle:    "bg-amber-500",
  offline: "bg-slate-400",
  unknown: "bg-slate-300",
};

// Accept a hex-ish color string for the accent; otherwise fall back to brand.
function safeHex(v: any, fallback: string): string {
  if (typeof v !== "string") return fallback;
  const t = v.trim();
  if (!/^#?[0-9a-fA-F]{3,8}$/.test(t)) return fallback;
  return t.startsWith("#") ? t : `#${t}`;
}

function SpecialistStatusRenderer({ component, records, screen, workspaceId, schema }: { component: UIComponent; records?: any[]; screen: ScreenTemplate; workspaceId: string; schema?: OSSchema }) {
  const dm = (component.data_mapping || {}) as Record<string, string>;
  const label = dm.label || "Specialists";
  const brandColor = schema?.metadata?.brand_color || DEFAULT_BRAND_COLOR;
  const source = records ?? [];

  // R1 — honest empty state. Never fabricate specialists.
  if (source.length === 0) {
    return (
      <GridEmptyState
        icon={<Network className="w-5 h-5" />}
        label="No specialists configured yet"
        hint="Each function you route to will appear here as a specialist."
      />
    );
  }

  const fields: ScreenField[] =
    (screen.fields && screen.fields.length > 0) ? screen.fields : inferFields(source[0]);
  const nameKey = dm.name || fields[0]?.key || "name";
  const statusKey = dm.status;
  const descKey = dm.description;
  const colorKey = dm.color;

  return (
    <div className="col-span-full">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
        <Network className="w-3.5 h-3.5" /> {label}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {source.map((record: any, idx: number) => {
          const name = String(record[nameKey] ?? "—");
          const statusRaw = statusKey ? record[statusKey] : null;
          const hasStatus = statusRaw != null && String(statusRaw).trim() !== "";
          const state = hasStatus ? specialistState(statusRaw) : "unknown";
          const description = descKey ? record[descKey] : null;
          const accent = colorKey ? safeHex(record[colorKey], brandColor) : brandColor;
          return (
            <div
              key={record.id ?? `${name}-${idx}`}
              onClick={() => useOsStore.getState().openDrawer(record, screen, workspaceId)}
              className="relative bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-all cursor-pointer group"
            >
              {/* Accent bar = identity (per-card color or brand) */}
              <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent }} aria-hidden="true" />
              <div className="p-5 pl-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                    {name}
                  </div>
                  {/* Status dot = state (semantic). Label only for a known
                      status; unknown/absent → neutral dot, no guessed label. */}
                  <span className="inline-flex items-center gap-1.5 shrink-0">
                    <span className={`w-2 h-2 rounded-full ${SPECIALIST_DOT[state]}`} aria-hidden="true" />
                    {hasStatus && state !== "unknown" && (
                      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                        {String(statusRaw)}
                      </span>
                    )}
                  </span>
                </div>
                {description != null && String(description).trim() !== "" && (
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">{String(description)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// INTEGRATION LIST — compact summary of MCP connections + link to the
// full IntegrationHub.
function IntegrationListRenderer({ workspaceId, onManage }: { workspaceId: string; onManage?: () => void }) {
  const [count, setCount] = useState<number | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/mcp/connections?workspace_id=${encodeURIComponent(workspaceId)}`);
        if (!res.ok) { if (!cancelled) setErrored(true); return; }
        const j = await res.json();
        if (cancelled) return;
        setCount(Array.isArray(j.connections) ? j.connections.length : 0);
      } catch {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  const countDisplay = errored ? "—" : count === null ? "…" : String(count);

  return (
    <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-6 flex items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
          <Plug className="w-5 h-5 text-violet-500" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Integrations</div>
          <div className="text-sm font-medium text-slate-900 mt-1">
            {countDisplay} {count === 1 ? "tool" : "tools"} connected
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onManage}
        disabled={!onManage}
        title={onManage ? "Open integrations" : "Use the Integrations screen in the sidebar"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed shrink-0"
      >
        Manage integrations <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
